import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../lib/auth/axiosApi";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

// Trading-strategy authoring + backtesting (DD-5/DD-9). Authoring is a chat:
// describe what you want, get a proposed spec + generated code, read it, then
// confirm it into an immutable version. Backtests read data and place nothing;
// live placement is stubbed (DD-4), so going "live" only records intents.

const label = { color: "#bbb", fontSize: 12, marginBottom: 2 };
const input = {
  padding: "6px 8px",
  borderRadius: 4,
  border: "1px solid #444",
  background: "#1e1e1e",
  color: "#eee",
};
const btn = {
  padding: "6px 12px",
  borderRadius: 4,
  border: "none",
  background: "#ffd23f",
  color: "#1a1a1a",
  fontWeight: 600,
  cursor: "pointer",
};
const btnGhost = { ...btn, background: "#333", color: "#eee" };
const card = { background: "#1b1b1b", borderRadius: 6, padding: 12, marginBottom: 14 };

const STATE_COLORS = { draft: "#555", live: "#1f6f3f", paused: "#8a6d1f", archived: "#444" };

function errOf(e) {
  return e?.response?.data?.error || e.message || "request failed";
}

function StrategyPage() {
  const [strategies, setStrategies] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // authoring
  const [message, setMessage] = useState("");
  const [proposal, setProposal] = useState(null);
  const [showCode, setShowCode] = useState(false);

  // backtest
  const [cash, setCash] = useState(10000);
  const [run, setRun] = useState(null);

  // limits (local edit buffer)
  const [limits, setLimits] = useState(null);

  const loadList = useCallback(async () => {
    try {
      const r = await axiosInstance.get("strategies/");
      setStrategies(r.data.results || []);
    } catch (e) {
      setError(errOf(e));
    }
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await axiosInstance.get(`strategies/${id}/`);
      setDetail(r.data);
      setLimits({
        max_order_value: r.data.limits.max_order_value,
        max_position_value: r.data.limits.max_position_value,
        max_orders_per_day: r.data.limits.max_orders_per_day,
        dry_run: r.data.dry_run,
      });
    } catch (e) {
      setError(errOf(e));
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);
  useEffect(() => {
    setProposal(null);
    setRun(null);
    setShowCode(false);
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const guard = async (fn) => {
    setLoading(true);
    setError("");
    try {
      return await fn();
    } catch (e) {
      setError(errOf(e));
    } finally {
      setLoading(false);
    }
  };

  const createStrategy = () =>
    guard(async () => {
      const name = window.prompt("Name this strategy");
      if (!name) return;
      const r = await axiosInstance.post("strategies/", { name });
      await loadList();
      setSelectedId(r.data.id);
    });

  const sendChat = () =>
    guard(async () => {
      if (!message.trim()) return;
      const r = await axiosInstance.post(`strategies/${selectedId}/chat/`, { message });
      setProposal(r.data);
    });

  const confirmProposal = () =>
    guard(async () => {
      await axiosInstance.post(`strategies/${selectedId}/confirm/`, {
        spec: proposal.proposal.spec,
        model_id: proposal.model_id,
        prompt_version: proposal.prompt_version,
      });
      setProposal(null);
      setMessage("");
      await loadDetail(selectedId);
      await loadList();
    });

  const runBacktest = () =>
    guard(async () => {
      const r = await axiosInstance.post(`strategies/${selectedId}/backtest/`, { cash: Number(cash) });
      setRun(r.data);
    });

  const saveLimits = () =>
    guard(async () => {
      await axiosInstance.post(`strategies/${selectedId}/limits/`, limits);
      await loadDetail(selectedId);
    });

  const lifecycle = (verb, body) =>
    guard(async () => {
      await axiosInstance.post(`strategies/${selectedId}/${verb}/`, body || {});
      await loadDetail(selectedId);
    });

  const metrics = run?.metrics || {};
  const curve = metrics.equity_curve || [];

  return (
    <div style={{ display: "flex", height: "100%", color: "#eee", fontFamily: "system-ui, sans-serif" }}>
      {/* Sidebar: strategy list */}
      <div style={{ width: 220, borderRight: "1px solid #333", padding: 12, overflow: "auto", flexShrink: 0 }}>
        <button style={{ ...btn, width: "100%", marginBottom: 10 }} onClick={createStrategy}>
          + New strategy
        </button>
        {strategies.length === 0 && <div style={{ color: "#777", fontSize: 13 }}>No strategies yet.</div>}
        {strategies.map((s) => (
          <div
            key={s.id}
            onClick={() => setSelectedId(s.id)}
            style={{
              padding: "8px 10px",
              borderRadius: 4,
              marginBottom: 6,
              cursor: "pointer",
              background: s.id === selectedId ? "#2a2a2a" : "transparent",
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600 }}>{s.name}</div>
            <span
              style={{
                fontSize: 10,
                padding: "1px 6px",
                borderRadius: 8,
                background: STATE_COLORS[s.state] || "#555",
              }}
            >
              {s.state}
            </span>
          </div>
        ))}
      </div>

      {/* Main */}
      <div style={{ flex: 1, padding: 16, overflow: "auto" }}>
        {error && (
          <div style={{ background: "#3a1f1f", color: "#ffb4b4", padding: "8px 10px", borderRadius: 4, marginBottom: 12 }}>
            {error}
          </div>
        )}

        {!detail && <div style={{ color: "#777" }}>Pick a strategy, or make one.</div>}

        {detail && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <h2 style={{ margin: 0 }}>{detail.name}</h2>
              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: STATE_COLORS[detail.state] }}>
                {detail.state}
              </span>
              {detail.kill_switch && (
                <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, background: "#7a2b2b" }}>
                  kill switch ON
                </span>
              )}
            </div>
            <div style={{ color: "#999", fontSize: 12, marginBottom: 14 }}>
              {detail.universe?.length ? `Universe: ${detail.universe.join(", ")} · ` : ""}
              {detail.version_count} version(s)
              {detail.active_version ? ` · active v${detail.active_version}` : ""}
              {" · "}
              {detail.dry_run ? "dry-run" : "live-capable"}
            </div>

            {/* Authoring */}
            <div style={card}>
              <h4 style={{ margin: "0 0 8px" }}>Describe a change</h4>
              <textarea
                style={{ ...input, width: "100%", minHeight: 60, resize: "vertical" }}
                placeholder='e.g. "buy SPY when the 50-day SMA crosses above the 200-day, sell when it crosses back"'
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <div style={{ marginTop: 8 }}>
                <button style={btn} disabled={loading} onClick={sendChat}>
                  Propose
                </button>
              </div>

              {proposal && (
                <div style={{ marginTop: 12, borderTop: "1px solid #333", paddingTop: 12 }}>
                  <div style={{ fontSize: 14, marginBottom: 8 }}>{proposal.proposal.summary}</div>

                  {proposal.proposal.questions?.length > 0 && (
                    <div style={{ background: "#2a2714", padding: "6px 10px", borderRadius: 4, marginBottom: 8, fontSize: 13 }}>
                      <strong>Questions:</strong>
                      <ul style={{ margin: "4px 0 0 18px" }}>
                        {proposal.proposal.questions.map((q, i) => (
                          <li key={i}>{q}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div style={{ marginBottom: 8 }}>
                    {proposal.proposal.rules.map((r) => (
                      <div key={r.ordinal} style={{ fontSize: 13, padding: "3px 0" }}>
                        <span style={{ color: "#ffd23f" }}>#{r.ordinal + 1}</span> [{r.kind}] {r.restatement}
                      </div>
                    ))}
                  </div>

                  <div style={{ fontSize: 12, marginBottom: 8 }}>
                    Static analysis:{" "}
                    <strong style={{ color: proposal.static_analysis?.passed ? "#5cd68a" : "#ff8f8f" }}>
                      {proposal.static_analysis?.passed ? "passed" : "FAILED"}
                    </strong>
                    {proposal.static_analysis?.errors?.length > 0 && (
                      <span style={{ color: "#ff8f8f" }}> — {proposal.static_analysis.errors.join("; ")}</span>
                    )}
                  </div>

                  {proposal.review_warning && (
                    <div style={{ background: "#3a2a14", color: "#ffd9a0", padding: "6px 10px", borderRadius: 4, marginBottom: 8, fontSize: 12 }}>
                      ⚠ {proposal.review_warning}
                      {proposal.description_mismatches?.map((m, i) => (
                        <div key={i}>• {typeof m === "string" ? m : JSON.stringify(m)}</div>
                      ))}
                    </div>
                  )}

                  <button style={btnGhost} onClick={() => setShowCode((s) => !s)}>
                    {showCode ? "Hide" : "Show"} generated code
                  </button>
                  {showCode && (
                    <pre
                      style={{
                        background: "#111",
                        color: "#cfe8ff",
                        padding: 10,
                        borderRadius: 4,
                        marginTop: 8,
                        fontSize: 12,
                        overflow: "auto",
                        maxHeight: 260,
                      }}
                    >
                      {proposal.proposal.generated_source}
                    </pre>
                  )}

                  <div style={{ marginTop: 10 }}>
                    <button
                      style={{ ...btn, opacity: proposal.static_analysis?.passed ? 1 : 0.5 }}
                      disabled={loading || !proposal.static_analysis?.passed}
                      onClick={confirmProposal}
                    >
                      Save as version
                    </button>
                    <span style={{ fontSize: 11, color: "#999", marginLeft: 8 }}>{proposal.note}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Backtest */}
            {detail.version_count > 0 && (
              <div style={card}>
                <h4 style={{ margin: "0 0 8px" }}>Backtest (latest version)</h4>
                <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 10 }}>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={label}>Starting cash ($)</span>
                    <input style={{ ...input, width: 120 }} type="number" value={cash} onChange={(e) => setCash(e.target.value)} />
                  </div>
                  <button style={btn} disabled={loading} onClick={runBacktest}>
                    Run backtest
                  </button>
                </div>

                {run && (
                  <>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                      <Stat label="Return" value={`${metrics.total_return_pct ?? "–"}%`} good={metrics.total_return_pct >= 0} />
                      <Stat label="Max drawdown" value={`${metrics.max_drawdown_pct ?? "–"}%`} good={false} />
                      <Stat label="Trades" value={metrics.trade_count ?? "–"} />
                      <Stat label="Ending equity" value={`$${metrics.ending_equity ?? "–"}`} />
                    </div>
                    {curve.length > 0 && (
                      <div style={{ width: "100%", height: 240 }}>
                        <ResponsiveContainer>
                          <LineChart data={curve} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                            <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                            <XAxis dataKey="date" tick={{ fill: "#999", fontSize: 10 }} minTickGap={40} />
                            <YAxis tick={{ fill: "#999", fontSize: 10 }} domain={["auto", "auto"]} width={60} />
                            <Tooltip contentStyle={{ background: "#1e1e1e", border: "1px solid #444", color: "#eee" }} />
                            <Line type="monotone" dataKey="equity" stroke="#5cd68a" dot={false} strokeWidth={2} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                    {run.status && (
                      <div style={{ fontSize: 11, color: "#999" }}>
                        run #{run.id} · {run.status}
                        {metrics.fill_model ? ` · ${metrics.fill_model}` : ""}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Risk limits + lifecycle */}
            {detail.version_count > 0 && limits && (
              <div style={card}>
                <h4 style={{ margin: "0 0 8px" }}>Risk limits & lifecycle</h4>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 10 }}>
                  <LimitInput label="Max order $" v={limits.max_order_value} onChange={(x) => setLimits({ ...limits, max_order_value: x })} />
                  <LimitInput label="Max position $" v={limits.max_position_value} onChange={(x) => setLimits({ ...limits, max_position_value: x })} />
                  <LimitInput label="Orders/day" v={limits.max_orders_per_day} onChange={(x) => setLimits({ ...limits, max_orders_per_day: x })} />
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                    <input type="checkbox" checked={!!limits.dry_run} onChange={(e) => setLimits({ ...limits, dry_run: e.target.checked })} />
                    dry-run
                  </label>
                  <button style={btnGhost} disabled={loading} onClick={saveLimits}>
                    Save limits
                  </button>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button style={{ ...btn, background: "#1f6f3f", color: "#fff" }} disabled={loading} onClick={() => lifecycle("activate")}>
                    Activate
                  </button>
                  <button style={btnGhost} disabled={loading} onClick={() => lifecycle("pause")}>
                    Pause
                  </button>
                  <button
                    style={{ ...btnGhost, background: detail.kill_switch ? "#7a2b2b" : "#333" }}
                    disabled={loading}
                    onClick={() => lifecycle("kill", { on: !detail.kill_switch })}
                  >
                    {detail.kill_switch ? "Release kill switch" : "Kill switch"}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: "#999", marginTop: 8 }}>
                  Live order placement is stubbed (DD-4) — activating records intents only, never a real trade.
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label: l, value, good }) {
  return (
    <div style={{ background: "#121212", borderRadius: 4, padding: "8px 12px", minWidth: 100 }}>
      <div style={{ fontSize: 10, color: "#999" }}>{l}</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: good === undefined ? "#eee" : good ? "#5cd68a" : "#ff8f8f" }}>{value}</div>
    </div>
  );
}

function LimitInput({ label: l, v, onChange }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <span style={label}>{l}</span>
      <input style={{ ...input, width: 110 }} type="number" value={v} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export default StrategyPage;
